<template>
  <!-- sm:p-6 is mirrored by POPUP_STACK_PADDING_PX in the script. -->
  <div
    class="pointer-events-none fixed inset-0 z-[60] flex items-end px-4 sm:items-start sm:p-6"
    :style="popupStackInset"
  >
    <!-- <div
      v-if="showRefreshPopup"
      id="popup-message-target"
      class="hidden w-full sm:flex flex-col items-center space-y-4 sm:items-end"
    >
    </div> -->
    <div
      id="popup-message-target"
      class="flex w-full flex-col items-center sm:items-end"
    >
      <!-- Its own polite region: progress toasts are news, not alerts. -->
      <SetupProgressHost v-if="authStore.isAuthed" />
      <div
        aria-live="assertive"
        class="flex w-full flex-col items-center gap-4 sm:items-end"
      >
        <NewVersionPopup
          :show="showRefreshPopup && !tourRunning"
          @refresh-page="$emit('refresh-page')"
        />
        <MissingResourcePopupMessage
          :required-resources="routeRequiredResources"
          :can-show="
            shouldShowMissingResourcePopup && hasRequiredData && !tourRunning
          "
        />
        <!-- Hidden, not unmounted, so a notice the user closed stays closed. -->
        <div v-show="!tourRunning" class="contents">
          <NoticePopup
            v-for="notice in notices.data"
            :key="notice.pk"
            :notice="notice"
          />
        </div>
      </div>
    </div>
  </div>
  <!-- sm:w-80 is mirrored by ANNOUNCEMENT_CARD_PX in the script. -->
  <div
    aria-live="polite"
    class="pointer-events-none fixed bottom-20 z-20 w-[calc(100vw-2rem)] sm:w-80 md:bottom-[max(1rem,env(safe-area-inset-bottom))]"
    :style="{ right: announcementRight }"
  >
    <AnnouncementPopup
      v-if="announcement && !announcementCovered && !tourRunning"
      :key="`${userId}-${announcement.pk}`"
      :notice="announcement"
      :pending="dismissalMatches && dismissal.isPending"
      :failed="dismissalMatches && dismissal.isError"
      @dismiss="dismissAnnouncement"
      @activate="dismissAnnouncement"
    />
  </div>
  <div
    class="pointer-events-none fixed right-(--feedback-right) bottom-20 z-20 w-[calc(100vw-1rem-var(--feedback-right))] sm:w-80 md:bottom-[max(1rem,env(safe-area-inset-bottom))]"
    :style="{ '--feedback-right': feedbackRight }"
  >
    <Transition v-bind="FEEDBACK_TRANSITION">
      <FeedbackSurvey
        v-if="showFeedback && shownSurvey"
        v-show="feedbackVisible"
        :survey="shownSurvey"
        :send-state="feedbackSendState"
        :draft="feedbackSeed"
        @accept="trackFeedback('feedback_survey_started')"
        @submit="submitFeedback"
        @progress="saveFeedbackDraft($event, userId)"
        @decline="declineFeedback"
        @retry="retryFeedback"
        @dismiss="dismissFeedback"
        @close="closeFeedbackAt"
      />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, shallowRef, toRef, watch } from "vue";
import { useDebounceFn, useTimeoutFn, useWindowSize } from "@vueuse/core";
import { RouteLocationNormalized, useRouter } from "vue-router";
import MissingResourcePopupMessage from "@/components/Core/PopupMessages/MissingResourcePopupMessage.vue";
import { useAuthStore } from "@/store/auth";
import { useOrganizationStore } from "@/store/organization";
import { ResourceChannel, useResourcesQuery } from "@/services/resources";
import { useTourRunning } from "@/composables/useGuidedTour";
import {
  useNoticesQuery,
  useAnnouncementsQuery,
  useDismissAnnouncementMutation,
} from "@/services/notices";
import { useProfileQuery } from "@/services/user";
import { useSidepanel } from "@/composables/useSidepanel";
import { useSidepanelDock } from "@/composables/useSidepanelDock";
import { sidebarWidthPx } from "@/composables/useSidebarDock";
import { useShellVisibility } from "@/composables/useShellVisibility";
import { useFeedbackSurveyPreview } from "@/composables/useFeedbackSurveyPreview";
import { useFeedbackSurveyHidden } from "@/composables/useHideFeedbackSurvey";
import AnnouncementPopup from "@/components/Core/PopupMessages/AnnouncementPopup.vue";
import FeedbackSurvey from "@/components/Core/PopupMessages/Feedback/FeedbackSurvey.vue";
import {
  FEEDBACK_DRAFT_DELAY_MS,
  FEEDBACK_SHOW_DELAY_MS,
  FEEDBACK_TRANSITION,
  FeedbackSendState,
  FeedbackSurveyDefinition,
  FeedbackSurveyDraft,
  FeedbackSurveyResult,
} from "@/components/Core/PopupMessages/Feedback/feedbackSurvey";
import { useTrack } from "@/services/gtm";
import {
  FeedbackSurveySubmission,
  useFeedbackSurveyStatusQuery,
  useSaveFeedbackDraftMutation,
  useSubmitFeedbackSurveyMutation,
} from "@/services/feedback";
import NewVersionPopup from "@/components/Core/PopupMessages/NewVersionPopup.vue";
import NoticePopup from "@/components/Core/PopupMessages/NoticePopup.vue";
import SetupProgressHost from "@/components/SetupProgress/SetupProgressHost.vue";
defineProps({ showRefreshPopup: { type: Boolean, default: false } });
defineEmits({
  "refresh-page": () => true,
});
const notices = useNoticesQuery();

const router = useRouter();
const shouldShowMissingResourcePopup = ref(false);
const tourRunning = useTourRunning();
const routeRequiredResources = ref<ResourceChannel[]>([]);

// The overlay can mount unauthenticated (new-version banner on /login);
// don't fire the resources query into 401s there.
const authStore = useAuthStore();
const profile = useProfileQuery();
const userId = computed(() =>
  authStore.isAuthed ? profile.data?.id : undefined,
);
const announcements = useAnnouncementsQuery(userId);
const dismissal = useDismissAnnouncementMutation();
// Below the dock breakpoint the sidepanel is a z-40 overlay that would hide
// the card (dismiss button included); the docked panel reserves its width
// via --sidepanel-width instead. Beside an open overlay the popups move left
// of it when they fit between it and the nav sidebar; the card hides if not.
const ANNOUNCEMENT_CARD_PX = 320; // the card's sm:w-80
const ANNOUNCEMENT_GAP_PX = 16; // the card's 1rem right offset, mirrored left
// Moved stack popups (w-full max-w-lg) shrink to the room between the nav
// sidebar and the overlay; narrower than this they stop reading well.
const POPUP_MIN_PX = 320;
const POPUP_STACK_PADDING_PX = 24; // the stack's sm:p-6, each side
const sidepanel = useSidepanel();
const sidepanelDock = useSidepanelDock();
const { width: windowWidth } = useWindowSize();
const { hasApplicationHeader, shouldShowSidepanel } = useShellVisibility();
// isOpen outlives the panel on routes that unmount it (docs, reports, 404).
const sidepanelOverlayOpen = computed(
  () =>
    shouldShowSidepanel.value &&
    sidepanel.isOpen.value &&
    !sidepanelDock.isDocked.value,
);
const roomBesideOverlay = computed(
  () => windowWidth.value - sidebarWidthPx.value - sidepanel.panelWidth.value,
);
const announcementCovered = computed(
  () =>
    sidepanelOverlayOpen.value &&
    roomBesideOverlay.value < ANNOUNCEMENT_CARD_PX + 2 * ANNOUNCEMENT_GAP_PX,
);
const popupStackInset = computed(() =>
  sidepanelOverlayOpen.value &&
  roomBesideOverlay.value >= POPUP_MIN_PX + 2 * POPUP_STACK_PADDING_PX
    ? {
        left: `${sidebarWidthPx.value}px`,
        right: sidepanel.panelMaxWidth.value,
      }
    : undefined,
);
const announcementRight = computed(() =>
  sidepanelOverlayOpen.value
    ? `calc(${sidepanel.panelMaxWidth.value} + 1rem)`
    : "calc(var(--sidepanel-width, 0px) + 1rem)",
);
const now = ref(Date.now());

watch(
  [() => announcements.data, userId],
  ([notices], _previous, onCleanup) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    function updateTime() {
      now.value = Date.now();
      const nextChange = Math.min(
        ...(notices ?? [])
          .map((notice) => Date.parse(notice.end) + 1)
          .filter((time) => time > now.value),
      );
      if (Number.isFinite(nextChange)) {
        timer = setTimeout(
          updateTime,
          Math.min(nextChange - now.value, 2_147_483_647),
        );
      }
    }
    updateTime();
    onCleanup(() => clearTimeout(timer));
  },
  { immediate: true },
);

const announcement = computed(() =>
  userId.value === undefined
    ? undefined
    : announcements.data?.find((notice) => Date.parse(notice.end) >= now.value),
);
const dismissalMatches = computed(
  () =>
    dismissal.variables?.userId === userId.value &&
    dismissal.variables?.noticeId === announcement.value?.pk,
);

const feedbackPreview = useFeedbackSurveyPreview();
const feedbackHidden = useFeedbackSurveyHidden();
const feedbackStatus = useFeedbackSurveyStatusQuery(userId);
const feedbackSubmission = useSubmitFeedbackSurveyMutation();
const feedbackDraftSave = useSaveFeedbackDraftMutation();
// Latched, so the status flipping to show:false after the POST can't pull the
// card away mid-thanks.
const feedbackOpen = shallowRef(false);
const feedbackEligible = computed(
  () =>
    userId.value !== undefined &&
    feedbackStatus.data?.show === true &&
    !!feedbackStatus.data.survey &&
    !feedbackOpen.value &&
    !feedbackPreview.active.value &&
    !feedbackDone.value &&
    !feedbackHidden.value &&
    hasApplicationHeader.value &&
    !announcement.value &&
    !tourRunning.value,
);
const feedbackDelay = useTimeoutFn(
  () => {
    feedbackSurvey.value = feedbackStatus.data?.survey ?? undefined;
    feedbackSeed.value = feedbackStatus.data?.draft ?? undefined;
    savedComment = {
      comment: feedbackSeed.value?.comment ?? "",
      followUp: feedbackSeed.value?.followUp ?? false,
    };
    unconfirmedComment = undefined;
    commentUnknown = false;
    feedbackOpen.value = true;
  },
  FEEDBACK_SHOW_DELAY_MS,
  { immediate: false },
);
// So a failed POST after closing can still offer a retry.
const feedbackCloseRequested = shallowRef(false);
// So a dismissed card doesn't come back this session.
const feedbackDone = shallowRef(false);
let feedbackPosted = false;
let feedbackShownTracked = false;
type FeedbackPost = Omit<FeedbackSurveySubmission, "survey">;
let lastSubmission: FeedbackPost | undefined;
let queuedDraft:
  | { result: FeedbackSurveyResult; forUserId: number }
  | undefined;
let savedComment = { comment: "", followUp: false };
// A comment a draft sent without a confirmed save (in flight or failed). The
// server may still have it, so a later save can't assume savedComment.
let unconfirmedComment: { comment: string; followUp: boolean } | undefined;
// Set after a comment save fails, since the server's copy is then unknown.
let commentUnknown = false;
// The survey and draft the open card was seeded with; refetches mustn't
// change them.
const feedbackSurvey = shallowRef<FeedbackSurveyDefinition>();
const feedbackSeed = shallowRef<FeedbackSurveyDraft>();
// The mutation's isPending is still true inside its own onSettled.
let draftInFlight = false;
// The overlay outlives a logout while the new-version banner is up, so the
// next account must start from scratch.
watch(userId, () => {
  feedbackDelay.stop();
  feedbackOpen.value = false;
  feedbackCloseRequested.value = false;
  feedbackDone.value = false;
  feedbackPosted = false;
  feedbackShownTracked = false;
  lastSubmission = undefined;
  queuedDraft = undefined;
  feedbackSeed.value = undefined;
  feedbackSurvey.value = undefined;
  feedbackSubmission.reset();
});
watch(
  feedbackEligible,
  (eligible) => {
    if (eligible) feedbackDelay.start();
    else feedbackDelay.stop();
  },
  { immediate: true },
);
const shownSurvey = computed(() =>
  feedbackPreview.active.value
    ? (feedbackStatus.data?.survey ?? undefined)
    : feedbackSurvey.value,
);
const showFeedback = computed(
  () =>
    userId.value !== undefined &&
    shownSurvey.value !== undefined &&
    (feedbackOpen.value || feedbackPreview.active.value),
);
const feedbackSendState = computed<FeedbackSendState | undefined>(() => {
  if (feedbackPreview.active.value) return undefined;
  if (feedbackSubmission.isError) return "failed";
  if (feedbackSubmission.isSuccess) return "sent";
  return "pending";
});
// v-show, not v-if: a half-written comment survives the sidepanel opening.
const feedbackVisible = computed(
  () =>
    hasApplicationHeader.value &&
    !announcement.value &&
    !announcementCovered.value &&
    !feedbackHidden.value &&
    !tourRunning.value &&
    !(feedbackCloseRequested.value && feedbackSendState.value !== "failed"),
);
watch(
  () => feedbackOpen.value && feedbackVisible.value,
  (visible) => {
    if (!visible || feedbackShownTracked) return;
    feedbackShownTracked = true;
    trackFeedback("feedback_survey_shown");
  },
);
// The undocked sidepanel's edge buttons hug the viewport edge; stay clear of them.
const feedbackRight = computed(() => {
  if (sidepanelOverlayOpen.value) return announcementRight.value;
  return shouldShowSidepanel.value && !sidepanelDock.isDocked.value
    ? "4.5rem"
    : "calc(var(--sidepanel-width, 0px) + 1rem)";
});

const track = useTrack();

function trackFeedback(event: string, params: Record<string, string> = {}) {
  if (!feedbackPreview.active.value) track(event, params);
}

function postFeedback(submission: FeedbackPost) {
  const survey = feedbackSurvey.value;
  if (
    feedbackPosted ||
    feedbackPreview.active.value ||
    userId.value === undefined ||
    survey === undefined
  ) {
    return;
  }
  feedbackPosted = true;
  lastSubmission = submission;
  feedbackSubmission.mutate(
    {
      userId: userId.value,
      submission: {
        ...submission,
        survey: survey.id,
        pagePath: router.currentRoute.value.path,
      },
    },
    {
      onSuccess: () => {
        if (submission.status === "completed") trackCompleted(submission);
      },
      onError: () => {
        feedbackPosted = false;
      },
    },
  );
}

// Counted on success, so a failed or retried send isn't a completion.
function trackCompleted(submission: FeedbackPost) {
  trackFeedback("feedback_survey_completed", {
    label: submission.followUp ? "follow_up" : "no_follow_up",
  });
  if (!submission.comment) trackFeedback("feedback_survey_last_step_skipped");
}

// Debounced so typing a comment doesn't post every keystroke.
const saveFeedbackDraft = useDebounceFn(
  (result: FeedbackSurveyResult, forUserId: number | undefined) => {
    if (feedbackPreview.active.value || forUserId === undefined) return;
    sendFeedbackDraft(result, forUserId);
  },
  FEEDBACK_DRAFT_DELAY_MS,
);

// One draft in flight at a time, so an older one can't land after a newer one.
// The comment and follow-up only go along when they differ from what this tab
// last saved or resumed with, so a stale tab can't clear another tab's.
function sendFeedbackDraft(result: FeedbackSurveyResult, forUserId: number) {
  const survey = feedbackSurvey.value;
  if (feedbackPosted || forUserId !== userId.value || !survey) return;
  if (draftInFlight) {
    queuedDraft = { result, forUserId };
    return;
  }
  const comment = { comment: result.comment, followUp: result.followUp };
  const commentChanged = commentNeedsSending(comment);
  draftInFlight = true;
  if (commentChanged) unconfirmedComment = comment;
  feedbackDraftSave.mutate(
    {
      survey: survey.id,
      status: "draft",
      answers: result.answers,
      ...(commentChanged && comment),
      pagePath: router.currentRoute.value.path,
    },
    {
      onSuccess: () => {
        if (commentChanged && forUserId === userId.value) {
          savedComment = comment;
          commentUnknown = false;
          if (unconfirmedComment === comment) unconfirmedComment = undefined;
        }
      },
      onError: () => {
        if (commentChanged && forUserId === userId.value) commentUnknown = true;
      },
      onSettled: () => {
        draftInFlight = false;
        const next = queuedDraft;
        queuedDraft = undefined;
        if (next) sendFeedbackDraft(next.result, next.forUserId);
      },
    },
  );
}

function commentNeedsSending(current: { comment: string; followUp: boolean }) {
  return (
    commentUnknown ||
    commentDiffers(current, savedComment) ||
    (unconfirmedComment !== undefined &&
      commentDiffers(current, unconfirmedComment))
  );
}

function commentDiffers(
  a: { comment: string; followUp: boolean },
  b: { comment: string; followUp: boolean },
) {
  return a.comment !== b.comment || a.followUp !== b.followUp;
}

function retryFeedback() {
  if (lastSubmission) postFeedback(lastSubmission);
}

function submitFeedback(result: FeedbackSurveyResult) {
  // So a stale tab's Close can't overwrite another tab's saved comment.
  const commentChanged = commentNeedsSending(result);
  postFeedback({
    status: result.completed ? "completed" : "partial",
    answers: result.answers,
    ...((result.completed || commentChanged) && {
      comment: result.comment,
      followUp: result.followUp,
    }),
  });
}

function declineFeedback() {
  trackFeedback("feedback_survey_declined");
  closeFeedback();
}

function closeFeedbackAt(step: number | undefined) {
  if (step !== undefined) {
    trackFeedback("feedback_survey_closed", {
      label: feedbackSurvey.value?.questions[step]?.id ?? "unknown",
      value: String(step + 1),
    });
  }
  closeFeedback();
}

function closeFeedback() {
  if (feedbackPreview.active.value) {
    feedbackPreview.finish();
    return;
  }
  postFeedback({ status: "declined" });
  feedbackCloseRequested.value = true;
}

function dismissFeedback() {
  feedbackOpen.value = false;
  feedbackCloseRequested.value = false;
  feedbackDone.value = true;
}

watch(
  [feedbackCloseRequested, feedbackSendState],
  ([closeRequested, sendState]) => {
    if (closeRequested && sendState === "sent") dismissFeedback();
  },
);

function dismissAnnouncement() {
  if (
    !announcement.value ||
    userId.value === undefined ||
    (dismissalMatches.value && dismissal.isPending)
  ) {
    return;
  }
  dismissal.mutate({ noticeId: announcement.value.pk, userId: userId.value });
}

const resources = useResourcesQuery(() => authStore.isAuthed);
const channelResources = computed(() => {
  return resources.data?.map((resource) => resource.channel) ?? [];
});

function maybeShowMissingResourcePopup(r: RouteLocationNormalized): void {
  routeRequiredResources.value = [...(r.meta.requiredResources ?? [])];
  if (routeRequiredResources.value.length > 0) {
    shouldShowMissingResourcePopup.value = routeRequiredResources.value.some(
      (r: ResourceChannel) => channelResources.value.includes(r),
    );
  }
}
const organizationStore = useOrganizationStore();

// Don't show the MissingResource popup if there's something else preventing the route from working
const hasRequiredData = toRef(organizationStore, "routeHasRequiredData");

watch(channelResources, () => {
  maybeShowMissingResourcePopup(router.currentRoute.value);
});

router.beforeEach(() => {
  organizationStore.setRouteHasRequiredData(true);
});

router.afterEach((to, _from, _failure) => {
  maybeShowMissingResourcePopup(to);
});
</script>
